import { getDeviceUuid } from 'app/services/secureTokenStore';

import {
  ACCOUNTS_API_REFRESH_TOKEN,
  ADMIN_PANEL_CLIENT_ID,
  PASSKEY_ASSERTION_OPTIONS_ROUTE,
  PASSKEY_DELETE_ROUTE,
  PASSKEY_LIST_ROUTE,
  PASSKEY_REGISTER_OPTIONS_ROUTE,
  PASSKEY_REGISTER_ROUTE,
  SET_INITIAL_PASSWORD_ROUTE,
  SIGN_IN_CREATE_REQUEST_ROUTE,
  SIGN_IN_PASSKEY_ROUTE,
  SIGN_IN_WITH_CREDENTIALS,
  SIGN_IN_WITH_OTP_REQUEST_ROUTE,
  SIGN_IN_WITH_PASSWORD_ROUTE,
  SIGN_METHOD_ROUTE,
  SIGN_OUT_DEVICE_ROUTE,
  SING_IN_VIA_TYPE,
  TRUST_DEVICE_ROUTE,
} from 'constants/auth';
import { getCompanyId, getDomainName } from 'utils/domain';

import { getRequestSourceMode } from './auth.utils';
import { SKIP_FORBIDDEN } from './httpConfig';

export class AuthApi {
  #http;

  constructor(http) {
    this.#http = http;
  }

  async getTenant(params) {
    return this.#http.get('/api/tenant', { params });
  }

  async refreshToken(body, { clientId = ADMIN_PANEL_CLIENT_ID } = {}) {
    return this.#http.post(
      ACCOUNTS_API_REFRESH_TOKEN,
      {
        ...body,
        clientId,
      },
      { headers: { sourceMode: getRequestSourceMode() } },
    );
  }

  async trustDevice(config) {
    return this.#http.post(TRUST_DEVICE_ROUTE, null, {
      ...config,
      headers: {
        ...config?.headers,
        clientId: ADMIN_PANEL_CLIENT_ID,
        sourceMode: getRequestSourceMode(),
        deviceUuid: getDeviceUuid(),
      },
    });
  }

  async signOutDevice(deviceUuid) {
    return this.#http.post(SIGN_OUT_DEVICE_ROUTE, { deviceUuid });
  }

  async signInWithCredentials(body, config) {
    return this.#http.post(
      SIGN_IN_WITH_CREDENTIALS,
      {
        ...body,
        clientId: ADMIN_PANEL_CLIENT_ID,
      },
      {
        ...config,
        headers: {
          ...config?.headers,
          sourceMode: getRequestSourceMode(),
        },
      },
    );
  }

  async signInWithAuthContext(config, { clientId = ADMIN_PANEL_CLIENT_ID } = {}) {
    return this.#http.get('/api/authorization-context', {
      ...config,
      headers: {
        ...config?.headers,
        clientId,
      },
    });
  }

  async signInWithImpersonate(body, config) {
    return this.#http.post(
      '/api/signin/impersonate',
      {
        ...body,
        clientId: ADMIN_PANEL_CLIENT_ID,
      },
      {
        ...config,
        headers: {
          ...config.headers,
          clientId: ADMIN_PANEL_CLIENT_ID,
          sourceMode: getRequestSourceMode(),
        },
      },
    );
  }

  async signInWithImpersonationRequest(body, config) {
    return this.#http.post(
      '/api/signin/create-impersonation-request',
      {
        ...body,
        clientId: ADMIN_PANEL_CLIENT_ID,
        type: SING_IN_VIA_TYPE.IMPERSONATION,
      },
      {
        ...config,
        headers: {
          ...config.headers,
          clientId: ADMIN_PANEL_CLIENT_ID,
          sourceMode: getRequestSourceMode(),
        },
      },
    );
  }

  async signInRevertImpersonation(body, config) {
    return this.#http.post('/api/signin/revert-impersonation', body, {
      ...config,
      headers: {
        ...config.headers,
        targetClientId: ADMIN_PANEL_CLIENT_ID,
        clientId: ADMIN_PANEL_CLIENT_ID,
        sourceMode: getRequestSourceMode(),
      },
    });
  }

  async getSignInMethod({ domainname }) {
    return this.#http.get(SIGN_METHOD_ROUTE, {
      headers: { domainname },
    });
  }

  async signInWithPassword(body, { password }) {
    return this.#http.post(
      SIGN_IN_WITH_PASSWORD_ROUTE,
      {
        ...body,
        password,
        clientId: ADMIN_PANEL_CLIENT_ID,
      },
      {
        headers: {
          sourceMode: getRequestSourceMode(),
        },
        // A 403 here means the account has no access to this client (pre-auth, no session to
        // recover) — surface it inline in the login form instead of the global page-reload.
        ...SKIP_FORBIDDEN,
      },
    );
  }

  async createSignInRequest(
    { email, redirectUrl = window.location.origin },
    { clientId = ADMIN_PANEL_CLIENT_ID } = {},
  ) {
    return this.#http.post(
      SIGN_IN_CREATE_REQUEST_ROUTE,
      {
        type: SING_IN_VIA_TYPE.EMAIL,
        email,
        companyId: getCompanyId(),
        clientId,
        redirectUrl,
        domain: getDomainName(),
      },
      { headers: { sourceMode: getRequestSourceMode() }, ...SKIP_FORBIDDEN },
    );
  }

  // `clientId` defaults to the AdminPanel-typed-OTP flow, but a confirm for an invite-generated
  // activation link (Login's URL-param path) must override it to TPO_INVITE_CLIENT_ID — the
  // SignInRequest was created with that ClientId, and the backend 400s on a mismatch.
  async signInWithOtpRequest({
    requestId,
    code,
    rememberDevice,
    clientId = ADMIN_PANEL_CLIENT_ID,
  }) {
    return this.#http.post(
      SIGN_IN_WITH_OTP_REQUEST_ROUTE,
      {
        clientId,
        requestId,
        code,
        domain: getDomainName(),
      },
      {
        headers: {
          'X-Remember-Device': rememberDevice ? 'true' : 'false',
          sourceMode: getRequestSourceMode(),
        },
        ...SKIP_FORBIDDEN,
      },
    );
  }

  async getPasskeyAssertionOptions() {
    return this.#http.get(PASSKEY_ASSERTION_OPTIONS_ROUTE);
  }

  async signInWithPasskey(body) {
    return this.#http.post(
      SIGN_IN_PASSKEY_ROUTE,
      {
        ...body,
        clientId: ADMIN_PANEL_CLIENT_ID,
        sourceMode: getRequestSourceMode(),
      },
      SKIP_FORBIDDEN,
    );
  }

  // Post-auth (the token is carried by the http client); returns the WebAuthn credential-creation options.
  async getPasskeyRegistrationOptions() {
    return this.#http.get(PASSKEY_REGISTER_OPTIONS_ROUTE);
  }

  async registerPasskey(body) {
    return this.#http.post(PASSKEY_REGISTER_ROUTE, body);
  }

  // Post-auth management: list enrolled passkeys and revoke individual ones.
  async getPasskeys() {
    return this.#http.get(PASSKEY_LIST_ROUTE);
  }

  async deletePasskey(id) {
    return this.#http.delete(`${PASSKEY_DELETE_ROUTE}/${id}`);
  }

  // DEV-4157: Called with the constrained activation token (NOT stored in jwtManager). The caller
  // supplies the token as an explicit Authorization header so the shared interceptor is bypassed.
  // On success the backend returns a full SecurityToken (access + refresh) → completeSignIn.
  async setInitialPassword({ newPassword, activationToken }) {
    return this.#http.post(
      SET_INITIAL_PASSWORD_ROUTE,
      { newPassword },
      { headers: { Authorization: `Bearer ${activationToken}` } },
    );
  }
}
